import React = require("react");

import PaintSource from "../core/PaintSource";
import { PaintCrop } from "../core/PaintCrop";
import Model from "../core/Model";
import { normalizedContextDrawImage } from "../Utils";

interface CanvasProps extends React.Props<Canvas> {
    top: number;
    left: number;
    width: number;
    height: number;
    model: Model;
    sourceImage: HTMLImageElement | HTMLCanvasElement;
    zIndex: number;
    mediaType: string;
    position: string;
    showSourceImage?: boolean;
    allowPan?: boolean;
    onPan(dX: number, dY: number): void;
    onPanStart?(): void;
    onPanEnd?(): void;
}

interface CanvasState {
    drag: boolean;
    my: number;
    mx: number;
    showSourceImage: boolean;
}

export default class Canvas extends React.Component<CanvasProps, CanvasState> {
    private canvas: HTMLCanvasElement;
    private grayscaleCanvas: HTMLCanvasElement;
    // optimization attempt
    // private zoomedGrayscaleCanvas: HTMLCanvasElement;

    private dragRunning: boolean;

    private initialDocHeight: number;
    private initialCropHeight: number;
    private prevCropHeight: number;

    constructor(props: CanvasProps) {
        super(props);
        this.state = {
            drag: false,
            mx: null,
            my: null,
            showSourceImage: props.showSourceImage
        };
    }

    componentWillReceiveProps(nextProps: CanvasProps) {
        if (nextProps.showSourceImage !== this.state.showSourceImage) {
            const state = {} as CanvasState;
            state.showSourceImage = nextProps.showSourceImage;
            this.setState(state);
        }
    }

    componentDidMount() {
        this.initialDocHeight = document.documentElement.scrollHeight;
        this.initialCropHeight = this.props.height;
        this.prevCropHeight = this.initialCropHeight;
        this.createGrayscaleCanvas();
        this.paint();
        this.canvas.addEventListener('mousemove', this.handleMouseMove);
    }

    componentWillUnmount() {
        this.removeDragEvents();
        this.removeTouchDragEvents();
        this.canvas.removeEventListener('mousemove', this.handleMouseMove);
    }

    componentDidUpdate(prevProps: CanvasProps) {
        if (this.props.sourceImage !== prevProps.sourceImage) {
            this.createGrayscaleCanvas();
        }
        this.paint();
        this.prevCropHeight = this.props.height;
    }

    paint() {
        this.doPaint();
    }

    doPaint = () => {
        if (this.canvasNeedsResize()) {
            this.resizeCanvas();
        }
        const showSource = this.state.showSourceImage;
        const top = showSource ? Math.round(this.props.top) : 0;
        const left = showSource ? Math.round(this.props.left) : 0;
        if (showSource) {
            PaintSource(this.canvas, this.grayscaleCanvas, this.props.model, left, top);
        }
        const clearCanvas = !showSource;
        PaintCrop(this.canvas, this.props.sourceImage, this.props.model, left, top, 1, clearCanvas);
    };

    exportImageData() {
        const tempCanvas = document.createElement("canvas");
        const tempContext = tempCanvas.getContext("2d");

        tempContext.canvas.width = this.props.width;
        tempContext.canvas.height = this.props.height;
        tempContext.drawImage(
            this.canvas,
            this.props.left,
            this.props.top,
            this.props.width,
            this.props.height,
            0,
            0,
            this.props.width,
            this.props.height
        );
        return tempCanvas.toDataURL("image/" + this.props.mediaType.toLowerCase());
    }

    render() {
        const width = Math.floor(this.props.width);
        const height = Math.floor(this.props.height);
        const model = this.props.model;
        return (
            <canvas
                ref={(canvas) => {this.canvas = canvas;}}
                style={this.getStyle().root}
                onMouseDown={this.props.allowPan ? this.handleMouseDown : null}
                onTouchStart={this.props.allowPan ? this.handleTouchStart : null}
                width={this.state.showSourceImage ? document.documentElement.scrollWidth : width}
                height={this.state.showSourceImage ? document.documentElement.scrollHeight : height}
                data-crop-zoom={model.getZoom().toFixed(4)}
                data-crop-left={model.getCropLeft()}
                data-crop-top={model.getCropTop()}
            />
        );
    }

    private handleMouseMove = (event: MouseEvent) => {
        if (!this.state.drag) {
            const showSource = event.pageX >= this.props.left
                && event.pageX <= this.props.left + this.props.width
                && event.pageY >= this.props.top
                && event.pageY <= this.props.top + this.props.height
            ;
            if (!this.props.showSourceImage && this.state.showSourceImage !== showSource) {
                if (this.props.allowPan) {
                    const state = {} as CanvasState;
                    state.showSourceImage = showSource;
                    this.setState(state);
                }
            }
        }
    };

    private handleMouseDown = () => {
        this.addDragEvents();
        this.startDrag();
    };

    private handleTouchStart = (e: React.TouchEvent<{}>) => {
        // prevent page scroll on image pan
        e.preventDefault();
        this.addTouchDragEvents();
        this.startDrag();
    };

    private handleDragEnd = () => {
        this.removeDragEvents();
        this.stopDrag();
    };

    private handleTouchDragEnd = () => {
        this.removeTouchDragEvents();
        this.stopDrag();
    };

    private handleDrag = (e: MouseEvent) => {
        e.preventDefault(); // prevent actions like involuntary text selection
        this.drag(e.clientX, e.clientY);
    };

    private handleTouchDrag = (e: TouchEvent) => {
        this.drag(e.targetTouches[0].clientX, e.targetTouches[0].clientY);
    };

    private resizeCanvas() {
        this.canvas.height = this.initialDocHeight + (this.props.height - this.initialCropHeight);
    }

    private startDrag() {
        const newState = {} as CanvasState;
        newState.drag = true;
        newState.mx = null;
        newState.my = null;
        this.setState(newState);

        if (this.props.onPanStart) {
            this.props.onPanStart();
        }
    }

    private stopDrag() {
        if (this.state.drag) {
            const newState = {} as CanvasState;
            newState.drag = false;
            newState.mx = null;
            newState.my = null;
            this.setState(newState);
        }
        if (this.props.onPanEnd) {
            this.props.onPanEnd();
        }
    }

    private drag(x: number, y: number) {
        //TODO: See if we can improve raf and let only the flag in raf
        if (this.dragRunning) { return; }
        this.dragRunning = true;
        requestAnimationFrame(() => {
            let newState = { mx: x, my: y, drag: true} as CanvasState;
            if (this.state.mx && this.state.my) {
                this.props.onPan(this.state.mx - x, this.state.my - y);
            }
            this.setState(newState);
            this.dragRunning = false;
        });
    }

    private addDragEvents() {
        document.addEventListener('mousemove', this.handleDrag, false);
        document.addEventListener('mouseup', this.handleDragEnd, false);
    }

    private addTouchDragEvents() {
        document.addEventListener('touchmove', this.handleTouchDrag, false);
        document.addEventListener('touchend', this.handleTouchDragEnd, false);
        document.addEventListener('touchup', this.handleTouchDragEnd, false);
        document.addEventListener('touchcancel', this.handleTouchDragEnd, false);
    }

    private removeDragEvents() {
        document.removeEventListener('mousemove', this.handleDrag, false);
        document.removeEventListener('mouseup', this.handleDragEnd, false);
    }

    private removeTouchDragEvents() {
        document.removeEventListener('touchmove', this.handleTouchDrag, false);
        document.removeEventListener('touchend', this.handleTouchDragEnd, false);
        document.removeEventListener('touchup', this.handleTouchDragEnd, false);
        document.removeEventListener('touchcancel', this.handleTouchDragEnd, false);
    }

    private createGrayscaleCanvas() {
        let tempCanvas = document.createElement("canvas");
        let sourceImage = this.props.sourceImage;
        tempCanvas.width = sourceImage.width;
        tempCanvas.height = sourceImage.height;

        let tempContext = tempCanvas.getContext("2d");
        normalizedContextDrawImage(
            tempContext,
            sourceImage,
            0, 0, sourceImage.width, sourceImage.height,
            0, 0, tempCanvas.width, tempCanvas.height
        );

        const imageData = tempContext.getImageData(0, 0, tempCanvas.width, tempCanvas.height);
        const data = imageData.data;
        const len = data.length;

        for (let i = 0; i < len; i += 4) {
            let brightness = 0.34 * data[i] + 0.5 * data[i + 1] + 0.16 * data[i + 2];
            data[i] = brightness; // red
            data[i + 1] = brightness; // green
            data[i + 2] = brightness; // blue
        }

        // overwrite original image
        tempContext.putImageData(imageData, 0, 0);
        this.grayscaleCanvas = tempCanvas;
    }

    // optimization attempt
    // private createZoomedGrayscaleCanvas() {
    //     this.zoomedGrayscaleCanvas = document.createElement("canvas");
    //     const absZoom = this.props.model.getAbsoluteZoom();
    //     this.zoomedGrayscaleCanvas.width = Math.min(
    //         this.grayscaleCanvas.width, this.grayscaleCanvas.width * absZoom
    //     );
    //     this.zoomedGrayscaleCanvas.height = Math.min(
    //         this.grayscaleCanvas.height, this.grayscaleCanvas.height * absZoom
    //     );

    //     normalizedContextDrawImage(
    //         this.zoomedGrayscaleCanvas.getContext("2d"),
    //         this.grayscaleCanvas,
    //         0, 0, this.grayscaleCanvas.width, this.grayscaleCanvas.height,
    //         0, 0, this.zoomedGrayscaleCanvas.width, this.zoomedGrayscaleCanvas.height
    //     );
    // }

    // TODO improve this to detect only really problematic cases, like resize handles are outside of the canvas
    // and only then to adjust canvas height
    private canvasNeedsResize() {
        const docHeightChanged = this.initialDocHeight !== document.documentElement.scrollHeight;
        const cropHeightChanged = this.prevCropHeight !== this.props.height;
        return this.state.showSourceImage && (cropHeightChanged || docHeightChanged);
    }

    private getStyle() {
        const model = this.props.model;
        const zoom = model.getAbsoluteZoom();
        const fitWidth = this.props.width + 1 > model.getSourceWidth() * zoom;
        const fitHeight = this.props.height + 1 > model.getSourceHeight() * zoom;
        return {
            root: {
                position: this.props.position,
                left: this.state.showSourceImage ? 0 : this.props.left,
                top: this.state.showSourceImage ? 0 : this.props.top,
                zIndex: this.props.zIndex,
                cursor: (fitWidth && fitHeight) || !this.props.allowPan ? 'default' : 'move',
                transition: 'width 0.3s'
            }
        };
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/image-inline-editor/components/Canvas.tsx
 **/